import { reportError } from "utils/diagnostics";
import React, { BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Params, useParams } from "react-router-dom";
import EditIconOutlined from "@mui/icons-material/EditOutlined";
import { useTheme } from "@mui/material";
import { FieldValues } from "react-hook-form";
import updateEISoftwareHelper from "api/PendingActionsHelpers";
import { changeDeviceStatusFromUI, getDeviceStatus } from "api/DeviceHelpers";
import { showWarning, showSuccess } from "utils/notifications";
import useConfirm from "hooks/useConfirm";
import TableSelect from "components/table/table-select/table-select.component";
import { FormInput } from "components/common/Input";
import useForm from "components/form/useForm";
import { KeyedMutator } from "swr";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { BoldText, CommonText, ErrorText } from "components/common/text/text.component";
import LoadingSpinner from "components/LoadingSpinner/loading-spinner.component";
import { getDateString, nameValidationCheck } from "utils/util-functions";
import { compareSoftwareVersions, isMegaSoftware, pollUntil } from "utils/general";
import { REFRESH_VALUES_POLL_INTERVAL, REFRESH_VALUES_POLL_TIMEOUT } from "utils/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { TableRow } from "components/table/table-row/table-row.component";
import { SpinnerWrapper, StatusDot, SectionHeader, DetailGridWrapper } from "./DeviceDetail.style";
import { DeviceValueTypes } from "../../../devices.config";
import style from "./device-detail-form.module.css";
import { Popup } from "components/common/popup/popup.component";
import { ButtonText } from "components/common/Buttons";
import { getAllDevicePools } from "api/DevicePoolHelpers";
import { useAuth } from "hooks/useProvideAuth";

dayjs.extend(utc);

const EI_MIN_UPDATE_VERSION = "EI-2.3.58";
const SOFTWARE_VER_2_4_96 = "MegaFI-2.4.96";
const SOFTWARE_VER_2_4_994 = "MegaFI-2.4.994";
const SOFTWARE_VER_2_4_996 = "MegaFI-2.4.996";
const SOFTWARE_VER_3_3_0 = "MegaFi_2-v3.3.0";
const CURRENT_MODEM_FIRMWARE = "__current_modem_firmware__";
const REQUIRED_MODEM_VERSION_PARTS = ["M0R", "A0R", "C0R", "PPV"];

const hasCompleteModemStatus = (device: DeviceValueTypes): boolean => {
  const modemModel = device.modemModel?.trim();
  const modemVersion = device.modemFirmwareVersion || "";

  return Boolean(
    modemModel &&
      modemModel.toLowerCase() !== "unknown" &&
      REQUIRED_MODEM_VERSION_PARTS.every((part) =>
        new RegExp(`(?:^|\\s)${part}\\.[0-9]+(?:\\s|$)`, "i").test(modemVersion),
      ),
  );
};

interface DetailFormProps {
  deviceDetail: DeviceValueTypes;
  mutatePendingActions: KeyedMutator<any>;
  setIsLoadingDetail: (arg: boolean) => void;
  isLoadingDetail: boolean;
  mutateDetail: KeyedMutator<any>;
}

interface FormContentProps extends DetailFormProps {
  isEditing: boolean;
  setIsEditing: (arg: boolean) => void;
  setIsWarningOpen: (arg: boolean) => void;
  isPreparingModemFirmware: boolean;
  modemFirmwareRefreshFailed: boolean;
}

export interface TableOptionTypes {
  value: string;
  label: string;
}

const FormContent = ({
  deviceDetail,
  mutatePendingActions,
  setIsLoadingDetail,
  isEditing,
  setIsEditing,
  isLoadingDetail,
  mutateDetail,
  setIsWarningOpen,
  isPreparingModemFirmware,
  modemFirmwareRefreshFailed,
}: FormContentProps): JSX.Element => {
  const [formData, setFormData] = useState<DeviceValueTypes>(deviceDetail);

  useEffect(() => {
    setFormData((currentFormData) => ({
      ...currentFormData,
      online: deviceDetail.online,
      modemFirmwareVersion: deviceDetail.modemFirmwareVersion,
      modemModel: deviceDetail.modemModel,
      modemBand: deviceDetail.modemBand,
      availableModemFirmware: deviceDetail.availableModemFirmware,
    }));
  }, [deviceDetail]);

  const [isLongUpdate, setIsLongUpdate] = useState(false);
  const [isLongUpdatePopup, setLongUpdatePopup] = useState(false);

  const [poolList, setPoolList] = useState<any>([]);

  const { isDemoUser }: any = useAuth();

  const getPools = async () => {
    if (isDemoUser) {
      return;
    }

    const response = await getAllDevicePools();

    setPoolList(response.data);
  };

  useEffect(() => {
    getPools();
  }, []);

  const [getConfirmation, Confirmation] = useConfirm();
  const theme = useTheme();

  const { sn: serialNumber }: Readonly<Params<string>> = useParams();

  const availableVersions = useMemo((): TableOptionTypes[] => {
    if (!formData?.availableSW || !formData?.availableSW?.length) {
      return [];
    }
    const versions = [...formData.availableSW];

    return versions.map((version: string) => ({
      value: version,
      label: version,
    }));
  }, [formData]);

  const availableModemVersions = useMemo((): TableOptionTypes[] => {
    const options = formData?.availableModemFirmware || [];

    if (isPreparingModemFirmware) {
      return [{ value: CURRENT_MODEM_FIRMWARE, label: "Loading modem firmware..." }];
    }

    const currentBand =
      formData.modemBand === "B14" ? "Band 14" : formData.modemBand === "B28" ? "Band 28" : formData.modemBand;
    const currentPPV = formData.modemFirmwareVersion?.match(/(?:^|\s)(PPV\.[0-9]+)(?:\s|$)/i)?.[1];
    const currentC0R = formData.modemFirmwareVersion?.match(/(?:^|\s)(C0R\.[0-9]+)(?:\s|$)/i)?.[1];
    const currentDetails = [currentPPV, currentC0R].filter(Boolean).join(" / ");
    const currentLabel = [currentBand, currentDetails].filter(Boolean).join(" — ");
    const modemStatusComplete = hasCompleteModemStatus(formData);

    let selectedLabel = currentLabel ? `${currentLabel} (current)` : "Current modem firmware";
    if (!modemStatusComplete && modemFirmwareRefreshFailed) {
      selectedLabel = "Modem firmware unavailable — incomplete device data";
    } else if (modemStatusComplete && options.length === 0) {
      selectedLabel = `${selectedLabel} — no compatible update available`;
    }

    return [
      {
        value: CURRENT_MODEM_FIRMWARE,
        label: selectedLabel,
      },
      ...options.map(({ fileName, label }) => ({
        value: fileName,
        label,
      })),
    ];
  }, [formData, isPreparingModemFirmware, modemFirmwareRefreshFailed]);

  const poolOptions: TableOptionTypes[] = useMemo(() => {
    if (!poolList || !poolList.length) return [];
    return poolList.map(({ devicePool }: { devicePool: TableOptionTypes }) => ({
      value: devicePool,
      label: devicePool,
    }));
  }, [poolList]);

  const uptimeText = useMemo(() => {
    if (!formData.uptime) return "";
    const { days, hours, minutes, seconds } = formData.uptime;
    return [days && `${days}day`, hours && `${hours}h`, minutes && `${minutes}m`, seconds && `${seconds}s`]
      .filter((item) => !!item)
      .join(" ");
  }, [formData.uptime]);

  const saveFormChanges = useCallback(
    async (data: DeviceValueTypes) => {
      setLongUpdatePopup(false);

      const {
        apn: newApn,
        deviceAlias: newDeviceAlias,
        deviceDescription: newDeviceDescription,
        eiSoftwareVersion: newEiSoftwareVersion,
        devicePool: newDevicePool,
        modemFirmwareTarget: newModemFirmwareTarget,
      } = data;

      const newDeviceData: any = {};

      // check changed device data
      if (newApn !== formData.apn) newDeviceData.apn = newApn;
      if (newDevicePool !== formData.devicePool) newDeviceData.devicePool = newDevicePool;
      if (newDeviceDescription !== formData.deviceDescription) {
        newDeviceData.deviceDescription = newDeviceDescription;
        formData.deviceDescription = newDeviceDescription;
      }
      if (newDeviceAlias !== formData.deviceAlias) {
        formData.deviceAlias = data.deviceAlias;
        newDeviceData.deviceAlias = newDeviceAlias;
      }

      // if nothing changed
      const deviceSoftwareChanged = !!newEiSoftwareVersion && formData.eiSoftwareVersion !== newEiSoftwareVersion;
      const modemFirmwareChanged = !!newModemFirmwareTarget && newModemFirmwareTarget !== CURRENT_MODEM_FIRMWARE;
      if (!Object.keys(newDeviceData).length && !deviceSoftwareChanged && !modemFirmwareChanged) {
        showWarning("Nothing is changed on the form!");
        return null;
      }

      // init form reset data
      let formResetData = {
        ...formData,
      };

      setIsLoadingDetail?.(true);

      if (deviceSoftwareChanged || modemFirmwareChanged) {
        try {
          const versionResponse = await updateEISoftwareHelper({
            serialNumber: formData.eiSerialNumber,
            ...(deviceSoftwareChanged ? { softwareVersion: newEiSoftwareVersion } : {}),
            ...(modemFirmwareChanged ? { modemFirmware: newModemFirmwareTarget } : {}),
          });

          const { data: responseData } = versionResponse || {};
          if (responseData) {
            if (formData.online && deviceSoftwareChanged && modemFirmwareChanged) {
              showWarning(
                "Modem and device updates are in progress. Do not power off your device. Check the event log for completion.",
              );
            } else if (formData.online && modemFirmwareChanged) {
              showWarning(
                "Modem firmware update is in progress. Do not power off your device. Check the event log for completion.",
              );
            } else if (formData.online) {
              showWarning(
                "Software update in progress. Do not power off your device. Check event log below to see when the software update is complete!",
              );
            }
            mutatePendingActions?.();

            // this should be done from API response
            formResetData = {
              ...formResetData,
              ...(deviceSoftwareChanged ? { eiSoftwareVersion: newEiSoftwareVersion } : {}),
              modemFirmwareTarget: CURRENT_MODEM_FIRMWARE,
            };
          }
        } catch (error) {
          reportError("device_details.update_software", error);
          // if version API is failed
          setIsLoadingDetail?.(false);
          return formResetData;
        }
      }

      if (Object.keys(newDeviceData).length > 0) {
        try {
          const deviceResponse = await changeDeviceStatusFromUI({
            eiSerialNumber: formData.eiSerialNumber,
            ...newDeviceData,
          });

          const { data: responseData }: any = deviceResponse;

          setFormData({
            ...formData,
            ...responseData,
          });

          // refetch device detail
          mutateDetail?.();
          showSuccess("Successfully updated device details!");

          setIsLoadingDetail(false);
          setIsEditing(false);
          return {
            ...formResetData,
            ...data,
            modemFirmwareTarget: CURRENT_MODEM_FIRMWARE,
          };
        } catch (error) {
          reportError("device_details.save", error);
          // if Save fails, then revert all changes on the form
          // Error messages will be handled on Axios interceptor
          setIsLoadingDetail(false);
          return formResetData;
        }
      }

      setIsEditing(false);
      setIsLoadingDetail(false);
      return formResetData;
    },
    [formData, getConfirmation, setIsEditing, setIsLoadingDetail, mutatePendingActions, mutateDetail],
  );

  const onCancel = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const { register, control, errors, Form, watch }: FieldValues = useForm({
    defaultValues: {
      eiSoftwareVersion: formData.eiSoftwareVersion,
      deviceAlias: formData.deviceAlias || "",
      deviceDescription: formData.deviceDescription || "",
      apn: formData.apn,
      devicePool: formData?.devicePool,
      modemFirmwareTarget: CURRENT_MODEM_FIRMWARE,
    },
    isEditing,
    onSave: saveFormChanges,
    onCancel,
  });

  const currentSoftwareVersion = watch("eiSoftwareVersion");

  const checkSoftwareVersion = (e: BaseSyntheticEvent) => {
    const selectedSoftware = e.target.value;

    if (selectedSoftware === SOFTWARE_VER_2_4_96) setLongUpdatePopup(true);
    if (
      selectedSoftware === SOFTWARE_VER_2_4_994 ||
      selectedSoftware === SOFTWARE_VER_2_4_996 ||
      selectedSoftware === SOFTWARE_VER_3_3_0
    ) {
      return setIsWarningOpen(true);
    }
  };

  useEffect(() => {
    if (currentSoftwareVersion === SOFTWARE_VER_2_4_96) {
      return setIsLongUpdate(true);
    }

    return setIsLongUpdate(false);
  }, [watch("eiSoftwareVersion")]);

  function getDetailValue(value: any, label?: string) {
    if (value === "Nil" || !value || value === "Not Available") {
      return "";
    }

    if (label === "TX Power" || label === "RX Power") {
      return `${value} dBm`;
    }

    if (label === "software") {
      if (value.updateAvailable) {
        const { updateStatus } = value;
        let statusText = "";
        let statusColor = "var(--mega-color-error)";

        if (updateStatus) {
          if (updateStatus === "InProgress") {
            statusText = "Update In Progress";
            statusColor = "var(--mega-color-error)";
          } else if (updateStatus.startsWith("Scheduled")) {
            // Extract date from "Scheduled 2025-12-13 22:00:00" format
            const dateMatch = updateStatus.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/);
            let scheduledDate = "";
            if (dateMatch) {
              // Parse UTC date string and convert to local time using the same method as Last Checkin
              const utcDateString = dateMatch[1];
              const localDateString = getDateString(dayjs.utc(utcDateString).local());
              scheduledDate = localDateString;
            }
            statusText = scheduledDate ? `Update Available Scheduled ${scheduledDate}` : "Update Available Scheduled";
            statusColor = "var(--mega-color-error)";
          } else if (updateStatus === "Pending - Device is OFF") {
            statusText = "Update Available Pending - Device is OFF";
            statusColor = "var(--mega-color-error)";
          } else if (updateStatus.includes("Pending")) {
            statusText = "Update Available Pending";
            statusColor = "var(--mega-color-error)";
          } else {
            statusText = "Update Available";
          }
        } else {
          statusText = "Update Available";
        }

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", marginBottom: "4px" }}>
              <div style={{ paddingRight: "16px" }}>
                <CommonText>{value.eiSoftwareVersion}</CommonText>
              </div>
              <BoldText sx={{ color: "var(--mega-color-error)" }}>Update Available</BoldText>
            </div>
            {statusText !== "Update Available" && (
              <BoldText sx={{ color: statusColor, fontSize: "14px" }}>
                {statusText.replace("Update Available ", "")}
              </BoldText>
            )}
          </div>
        );
      }
      return value.eiSoftwareVersion;
    }

    return value;
  }

  return (
    <Form
      openPopup={checkSoftwareVersion}
      closePopup={() => setLongUpdatePopup(false)}
      isLongUpdate={isLongUpdate}
      isLongUpdatePopup={isLongUpdatePopup}
      isLoading={isEditing && isLoadingDetail}
    >
      <DetailGridWrapper className={style.detailGrid}>
        <TableRow label="Serial No.:" result={serialNumber} />

        <TableRow label="Current Status:" result="">
          <StatusDot
            style={{
              backgroundColor: formData.online ? theme.palette.success.main : theme.palette.error.main,
              marginRight: 5,
            }}
          />
          <BoldText className="results">{formData.online ? "Online" : "Offline"}</BoldText>
        </TableRow>

        <TableRow label="APN:" result={getDetailValue(formData.apn)} />

        <TableRow label="IMEI:" result={getDetailValue(formData.imei)} />

        <TableRow label="Last Checkin:" result={getDateString(formData.lastCheckIn)} />

        <TableRow label="LAN IP Address:" result={getDetailValue(formData.lanIpAddress)} />

        {isEditing ? (
          <div className={style.formContainer}>
            <TableRow label="Device Alias:" result="">
              <FormInput
                {...register("deviceAlias", {
                  validate: {
                    nameValidationCheck,
                  },
                })}
              />
            </TableRow>
            {errors.deviceAlias && <ErrorText className={style.padding}>{errors.deviceAlias.message}</ErrorText>}
          </div>
        ) : (
          <TableRow label="Device Alias:" result={getDetailValue(formData.deviceAlias)} />
        )}

        <TableRow label="Uptime:" result={getDetailValue(uptimeText)} />

        <TableRow label="WWAN IP Address:" result={getDetailValue(formData.wwanIpAddress)} />

        {isEditing ? (
          <div className={style.formContainer}>
            <TableRow label="Device Description:" result="">
              <FormInput
                {...register("deviceDescription", {
                  validate: {
                    nameValidationCheck,
                  },
                })}
              />
            </TableRow>
            {errors.deviceDescription && (
              <ErrorText className={style.padding}>{errors.deviceDescription.message}</ErrorText>
            )}
          </div>
        ) : (
          <TableRow label="Device Description:" result={getDetailValue(formData.deviceDescription)} />
        )}

        <TableRow label="SIM Phone Number:" result={getDetailValue(formData.phoneNumber)} />

        <TableRow label="TX Power" result={getDetailValue(formData.txPower, "TX Power")} />

        {isEditing && poolOptions ? (
          <TableRow label="Device Pool:" result="">
            <TableSelect name="devicePool" options={poolOptions} control={control} width="" />
          </TableRow>
        ) : (
          <TableRow label="Device Pool:" result={getDetailValue(formData.devicePool)} />
        )}

        {isEditing ? (
          <TableRow label="Software Version" result="">
            <TableSelect
              {...register("eiSoftwareVersion", {
                onChange: (e: BaseSyntheticEvent) => checkSoftwareVersion(e),
              })}
              name="eiSoftwareVersion"
              options={availableVersions}
              control={control}
              disabled={
                !formData.online &&
                (isMegaSoftware(deviceDetail.eiSoftwareVersion) ||
                  (deviceDetail.eiSoftwareVersion.toLowerCase().startsWith("ei-") &&
                    compareSoftwareVersions(deviceDetail.eiSoftwareVersion, EI_MIN_UPDATE_VERSION) > 0))
              }
              width=""
            />
          </TableRow>
        ) : (
          <TableRow label="Software Version" result={getDetailValue(deviceDetail, "software")} />
        )}

        {formData.modemFirmwareVersion && formData.modemFirmwareVersion !== "unknown" && (
          <>
            <TableRow label="Modem Model:" result={getDetailValue(formData.modemModel)} />
            <TableRow label="Modem Band:" result={getDetailValue(formData.modemBand)} />
          </>
        )}

        <TableRow label="RX Power" result={getDetailValue(formData.RxPower, "RX Power")} />

        <TableRow label="Network Mode:" result={getDetailValue(formData.netAccessMode)} />

        {formData.modemFirmwareVersion && formData.modemFirmwareVersion !== "unknown" && (
          <TableRow
            label="Modem Firmware:"
            result={getDetailValue(formData.modemFirmwareVersion)}
            fullWidth={!isEditing}
          >
            {isEditing && (
              <TableSelect
                name="modemFirmwareTarget"
                options={availableModemVersions}
                control={control}
                disabled={!formData.online || isPreparingModemFirmware || availableModemVersions.length === 1}
                width=""
              />
            )}
          </TableRow>
        )}
      </DetailGridWrapper>

      <Confirmation />
    </Form>
  );
};

const DetailForm = ({
  deviceDetail,
  mutatePendingActions,
  setIsLoadingDetail,
  isLoadingDetail,
  mutateDetail,
}: DetailFormProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isPreparingModemFirmware, setIsPreparingModemFirmware] = useState(false);
  const [modemFirmwareRefreshFailed, setModemFirmwareRefreshFailed] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { isDemoUser }: Record<string, string> = useAuth();

  const editingOnClick = async () => {
    setIsEditing(true);
    setModemFirmwareRefreshFailed(false);

    const canRefreshModemStatus =
      deviceDetail.online &&
      deviceDetail.eiSoftwareVersion?.startsWith("MegaFi_2") &&
      !hasCompleteModemStatus(deviceDetail);

    if (!canRefreshModemStatus) {
      return;
    }

    setIsPreparingModemFirmware(true);

    let refreshedDetail: any = null;
    let lastCheckInBefore = deviceDetail.lastCheckIn;
    try {
      const current = await getDeviceStatus(deviceDetail.eiSerialNumber).catch(() => null);
      lastCheckInBefore = current?.deviceData?.[0]?.lastCheckIn ?? lastCheckInBefore;

      await changeDeviceStatusFromUI({
        eiSerialNumber: deviceDetail.eiSerialNumber,
        getDeviceDetails: true,
      });

      const refreshed = await pollUntil(
        async () => {
          if (!isMountedRef.current) {
            return true;
          }

          const response = await getDeviceStatus(deviceDetail.eiSerialNumber).catch(() => null);
          const nextDeviceDetail = response?.deviceData?.[0];
          if (
            !nextDeviceDetail ||
            nextDeviceDetail.lastCheckIn === lastCheckInBefore ||
            !hasCompleteModemStatus(nextDeviceDetail)
          ) {
            return false;
          }

          refreshedDetail = response;
          return true;
        },
        REFRESH_VALUES_POLL_INTERVAL,
        REFRESH_VALUES_POLL_TIMEOUT,
      );

      if (!isMountedRef.current) {
        return;
      }

      if (refreshed && refreshedDetail) {
        await mutateDetail(refreshedDetail, false);
      } else {
        await mutateDetail();
        setModemFirmwareRefreshFailed(true);
      }
    } catch (_error) {
      if (isMountedRef.current) {
        setModemFirmwareRefreshFailed(true);
      }
    } finally {
      if (isMountedRef.current) {
        setIsPreparingModemFirmware(false);
      }
    }
  };

  return (
    <>
      <SectionHeader>
        <span className="header-title">Device Details</span>

        {!isLoadingDetail && !isEditing && !isDemoUser && (
          <NxtIconButton onClick={editingOnClick} text="Edit" icon={<EditIconOutlined />} />
        )}
      </SectionHeader>

      {deviceDetail && (
        <FormContent
          setIsWarningOpen={setIsWarningOpen}
          deviceDetail={deviceDetail}
          mutatePendingActions={mutatePendingActions}
          setIsLoadingDetail={setIsLoadingDetail}
          isLoadingDetail={isLoadingDetail}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          mutateDetail={mutateDetail}
          isPreparingModemFirmware={isPreparingModemFirmware}
          modemFirmwareRefreshFailed={modemFirmwareRefreshFailed}
        />
      )}

      <Popup
        warning
        primaryBtn={{ text: ButtonText.Close, onClick: () => setIsWarningOpen(false) }}
        title="Warning"
        open={isWarningOpen}
      >
        <CommonText>
          if your device is operating in Passthrough mode, please place the device in NAT mode prior to updating to this
          release
        </CommonText>
      </Popup>

      {!isEditing && isLoadingDetail && (
        <SpinnerWrapper>
          <LoadingSpinner />
        </SpinnerWrapper>
      )}
    </>
  );
};

export default DetailForm;
